import * as crypto from 'crypto';

import { INVOICE_ITEMS } from '../../core/constants/Billing';
import {
  AdjustmentLineItem,
  Billing,
  LineItem,
  NonPaymentLineItem,
  PaymentLineItem,
  VendorInvoice,
} from '../../types/shared/Vendor';

// Copied from datastore/src/support/createHexId.ts

// This will produce a randomly-generated 32-character hex ID
function getRandomBytes(n: number): Promise<Buffer> {
  return new Promise((resolve, reject) => {
    crypto.randomBytes(n, (error, result) => {
      error ? reject(error) : resolve(result);
    });
  });
}

async function createHexId() {
  let buffer = await getRandomBytes(16);
  return buffer.toString('hex');
}
// Copied from datastore/src/support/createHexId.ts

type ApplyInvoiceOptions = {
  onAddLineItem?: (lineItem: LineItem) => void;
};

const defaultOptions: ApplyInvoiceOptions = {};

export async function createEmptyInvoice(input: {
  vendorId: string;
  invoiceId?: string;
  now?: string;
}): Promise<VendorInvoice> {
  const now = input.now ?? new Date().toISOString();
  const { invoiceId, vendorId } = input;
  return {
    id: invoiceId ?? (await createHexId()),
    vendorId,
    totalPrice: 0,
    taxAmount: 0,
    adjustmentAmount: 0,
    adjustmentReason: '',
    invoicedLineItems: [],
    created_at: now,
  };
}

export function getDefaultBilling(now = new Date().toISOString()): Billing {
  return {
    runningTotal: 0,
    runningTotalTaxAmount: 0,
    amountPaid: 0,
    invoicedItems: [],
    invoices: [],
    modified_at: now,
  };
}

export function getTotals(invoicedItems: Array<LineItem> = []) {
  let subTotal = 0;
  let taxTotal = 0;
  let amountPaid = 0;
  let charges: Array<NonPaymentLineItem | AdjustmentLineItem> = [];
  let payments: Array<PaymentLineItem> = [];
  for (let lineItem of invoicedItems) {
    if (lineItem.type === 'adjustment') {
      charges.push(lineItem);
      subTotal += lineItem.amountInDollars;
      const taxRate = lineItem.isAdjustmentTaxable ? lineItem.taxPercent : 0;

      taxTotal += lineItem.amountInDollars * taxRate;
      continue;
    }
    if (lineItem.type === 'payment') {
      payments.push(lineItem);
      amountPaid += lineItem.amount;
      continue;
    }
    subTotal += lineItem.amount;
    taxTotal += lineItem.amount * lineItem.taxPercent;
    charges.push(lineItem);
  }
  let grandTotal = subTotal + taxTotal;
  amountPaid = amountPaid / 100;
  let remainingBalance = grandTotal - amountPaid;
  return { subTotal, taxTotal, grandTotal, amountPaid, remainingBalance };
}

function getNonPaymentInvoicedItems(
  billing: Billing,
): Array<NonPaymentLineItem> {
  return billing.invoicedItems.filter<NonPaymentLineItem>(
    (item): item is NonPaymentLineItem => item.type !== 'payment',
  );
}

export function applyInvoice(
  billing: Billing | undefined,
  invoice: VendorInvoice | undefined,
  options = defaultOptions,
): Billing {
  let { onAddLineItem } = options;
  let now = new Date().toISOString();
  let oldBilling = billing || getDefaultBilling(now);
  if (!invoice) {
    return oldBilling;
  }
  let vendorInvoice = invoice;
  for (let oldInvoice of oldBilling.invoices) {
    // Check if the invoice has already been applied. This shouldn't actually be necessary.
    if (oldInvoice.id === vendorInvoice.id) {
      return oldBilling;
    }
  }
  let newLineItems: Array<LineItem> = [];

  // Add signup/membership fee
  let oldSignupFeeLineItem = oldBilling.invoicedItems.find(
    // TODO (#1508): Use item.type === 'membership_fee'
    (item) =>
      item.type !== 'payment' &&
      item.type !== 'adjustment' &&
      item.name === 'Signup/Membership Fee',
  );
  if (!oldSignupFeeLineItem) {
    let newSignupFeeLineItem = vendorInvoice.invoicedLineItems.find(
      // TODO (#1508): Use item.type === 'membership_fee'
      (item) =>
        item.type !== 'payment' &&
        item.type !== 'adjustment' &&
        item.name === 'Signup/Membership Fee',
    );
    if (newSignupFeeLineItem) {
      newLineItems.push(newSignupFeeLineItem);
    }
  }

  for (const lineItem of vendorInvoice.invoicedLineItems) {
    if (lineItem.type === 'payment' || lineItem.type === 'adjustment') {
      continue;
    }
    if (
      // TODO (#1508): Use lineItem.type === 'market_charges'
      (lineItem.name === INVOICE_ITEMS.market_charges ||
        lineItem.type === 'addon' ||
        lineItem.type === 'flat_fee' ||
        lineItem.type === 'redemption' ||
        lineItem.type === 'pct_of_sales') &&
      lineItem.dates
    ) {
      let oldLineItems: Array<NonPaymentLineItem> = [];
      if (lineItem.name === INVOICE_ITEMS.market_charges) {
        oldLineItems = oldBilling.invoicedItems.filter<NonPaymentLineItem>(
          (item): item is NonPaymentLineItem => item.type !== 'payment',
        );
        oldLineItems.filter(
          // TODO: Use `type` here instead of `name`
          (old) =>
            old.name === INVOICE_ITEMS.market_charges &&
            old.marketId === lineItem.marketId,
        );
      } else if (lineItem.type === 'addon') {
        oldLineItems = getNonPaymentInvoicedItems(oldBilling).filter(
          // TODO: Use `type` here instead of `name`
          (old) => old.type === 'addon' && old.marketId === lineItem.marketId,
        );
      } else if (lineItem.type === 'flat_fee') {
        oldLineItems = getNonPaymentInvoicedItems(oldBilling).filter(
          (old) =>
            old.type === 'flat_fee' && old.marketId === lineItem.marketId,
        );
      } else if (lineItem.type === 'redemption') {
        oldLineItems = getNonPaymentInvoicedItems(oldBilling).filter(
          (old) =>
            old.type === 'redemption' && old.marketId === lineItem.marketId,
        );
      } else if (lineItem.type === 'pct_of_sales') {
        oldLineItems = getNonPaymentInvoicedItems(oldBilling).filter(
          (old) =>
            old.type === 'pct_of_sales' && old.marketId === lineItem.marketId,
        );
      }

      if (oldLineItems.length) {
        let oldDates = new Set<string>();
        for (let { dates } of oldLineItems) {
          dates?.forEach((date) => oldDates.add(date));
        }
        let newDates = lineItem.dates.filter((date) => !oldDates.has(date));
        if (newDates.length) {
          // The price per date here is based on the new line item, not the old one.
          let pricePerDate = lineItem.amount / lineItem.dates.length;
          let newLineItem: LineItem = {
            ...lineItem,
            amount: newDates.length * pricePerDate,
            dates: newDates,
          };
          newLineItems.push(newLineItem);
          onAddLineItem?.(newLineItem);
        }
      } else {
        newLineItems.push(lineItem);
        onAddLineItem?.(lineItem);
      }
    }
  }

  const taxPercent = vendorInvoice.taxPercent ? vendorInvoice.taxPercent : 0;

  let manualAdjustmentLineItem: LineItem = {
    name: `Adjustment: ${vendorInvoice.adjustmentReason}`,
    amount: vendorInvoice.adjustmentAmount,
    taxPercent: vendorInvoice.isAdjustmentTaxable ? taxPercent : 0,
    type: 'manual',
    created_at: now,
  };

  if (manualAdjustmentLineItem.amount !== 0) {
    newLineItems.push(manualAdjustmentLineItem);
  }

  let oldTotals = getTotals(oldBilling.invoicedItems);
  let updatedRunningTotal = oldTotals.subTotal;
  let updatedRunningTotalTaxAmount = oldTotals.taxTotal;

  for (let item of newLineItems) {
    if (item.type === 'adjustment') {
      updatedRunningTotal += item.amountInDollars;
      const taxRate = item.isAdjustmentTaxable ? item.taxPercent : 0;
      updatedRunningTotalTaxAmount += item.amountInDollars * taxRate;
      continue;
    }
    if (item.type === 'payment') {
      updatedRunningTotal -= item.amount / 100;
      continue;
    }
    updatedRunningTotal += item.amount;
    if (item.type === 'redemption') {
      continue;
    }
    updatedRunningTotalTaxAmount += item.amount * item.taxPercent;
  }

  return {
    ...oldBilling,
    runningTotal: updatedRunningTotal,
    runningTotalTaxAmount: updatedRunningTotalTaxAmount,
    invoicedItems: [...oldBilling.invoicedItems, ...newLineItems],
    invoices: [...oldBilling?.invoices, vendorInvoice],
    modified_at: now,
  };
}

export function getAllPaymentLineItems(invoices: Billing['invoices']) {
  const paymentLineItems =
    invoices.reduce<Array<PaymentLineItem>>((prev, curr) => {
      const invoicePaymentLineItems = curr.invoicedLineItems.filter(
        (item): item is PaymentLineItem => item.type === 'payment',
      );
      return [...prev, ...invoicePaymentLineItems];
    }, []) ?? [];
  return paymentLineItems;
}
